/**
 * Minimal ICC profile reader for matrix/TRC display profiles: description,
 * colorant primaries (un-adapted from the D50 PCS) and tone response curve.
 * Used for the automatic transfer-function / primaries detection.
 */
import { bradford, bt709InverseOetf, D50, mat3Inv, mat3Vec, PRIMARIES, srgbEotf, type Chromaticities, type Mat3 } from '../core/color';
import type { EncodingHint, PrimariesId } from '../core/types';

export interface IccInfo {
  description: string;
  colorSpace: string;
  version: number;
  primaries: Chromaticities | null;
  /** Classified tone curve; 'curve' carries uniform samples of the exact TRC. */
  trc: { kind: 'srgb' | 'linear' | 'gamma' | 'bt709' | 'curve'; gamma?: number; curve?: number[]; approx: boolean } | null;
}

function str4(d: DataView, o: number): string {
  return String.fromCharCode(d.getUint8(o), d.getUint8(o + 1), d.getUint8(o + 2), d.getUint8(o + 3));
}

function s15(d: DataView, o: number): number {
  return d.getInt32(o) / 65536;
}

function readText(d: DataView, o: number, size: number): string {
  const type = str4(d, o);
  if (type === 'desc') {
    const n = d.getUint32(o + 8);
    let s = '';
    for (let i = 0; i < n - 1 && o + 12 + i < d.byteLength; i++) s += String.fromCharCode(d.getUint8(o + 12 + i));
    return s;
  }
  if (type === 'mluc') {
    const nrec = d.getUint32(o + 8);
    if (nrec < 1) return '';
    // Prefer an English record.
    let rec = 0;
    for (let r = 0; r < nrec; r++) {
      const lang = str4(d, o + 16 + r * 12).slice(0, 2);
      if (lang === 'en') {
        rec = r;
        break;
      }
    }
    const len = d.getUint32(o + 16 + rec * 12 + 4);
    const off = d.getUint32(o + 16 + rec * 12 + 8);
    let s = '';
    for (let i = 0; i + 1 < len; i += 2) s += String.fromCharCode(d.getUint16(o + off + i));
    return s;
  }
  if (type === 'text') {
    let s = '';
    for (let i = 8; i < size; i++) {
      const c = d.getUint8(o + i);
      if (!c) break;
      s += String.fromCharCode(c);
    }
    return s;
  }
  return '';
}

/** Build the TRC as a function on [0,1]. */
function readCurve(d: DataView, o: number): ((x: number) => number) | null {
  const type = str4(d, o);
  if (type === 'curv') {
    const n = d.getUint32(o + 8);
    if (n === 0) return (x) => x;
    if (n === 1) {
      const g = d.getUint16(o + 12) / 256;
      return (x) => Math.pow(x, g);
    }
    const t = new Float64Array(n);
    for (let i = 0; i < n; i++) t[i] = d.getUint16(o + 12 + 2 * i) / 65535;
    return (x) => {
      const p = Math.min(Math.max(x, 0), 1) * (n - 1);
      const i = Math.min(n - 2, Math.floor(p));
      return t[i] + (p - i) * (t[i + 1] - t[i]);
    };
  }
  if (type === 'para') {
    const fn = d.getUint16(o + 8);
    const p = Array.from({ length: 7 }, (_, i) => (o + 12 + 4 * i + 4 <= d.byteLength ? s15(d, o + 12 + 4 * i) : 0));
    const [g, a, b, c, dd, e, f] = p;
    switch (fn) {
      case 0:
        return (x) => Math.pow(x, g);
      case 1:
        return (x) => (x >= -b / a ? Math.pow(a * x + b, g) : 0);
      case 2:
        return (x) => (x >= -b / a ? Math.pow(a * x + b, g) + c : c);
      case 3:
        return (x) => (x >= dd ? Math.pow(a * x + b, g) : c * x);
      case 4:
        return (x) => (x >= dd ? Math.pow(a * x + b, g) + e : c * x + f);
    }
  }
  return null;
}

/** Classify a tone curve as sRGB, linear or a best-fit power law. */
export function classifyCurve(fn: (x: number) => number): NonNullable<IccInfo['trc']> {
  const xs = Array.from({ length: 64 }, (_, i) => (i + 1) / 65);
  const maxErr = (g: (x: number) => number) => Math.max(...xs.map((x) => Math.abs(fn(x) - g(x))));
  if (maxErr((x) => x) < 2e-3) return { kind: 'linear', approx: false };
  // Least squares in log-log space over the mid range.
  let num = 0;
  let den = 0;
  for (const x of xs) {
    const y = fn(x);
    if (x < 0.05 || !(y > 0)) continue;
    num += Math.log(x) * Math.log(y);
    den += Math.log(x) ** 2;
  }
  const gamma = den ? num / den : 2.2;
  const gammaErr = maxErr((x) => Math.pow(x, gamma));
  const srgbErr = maxErr(srgbEotf);
  if (srgbErr < 1.5e-3 && srgbErr <= gammaErr) return { kind: 'srgb', approx: false };
  if (maxErr(bt709InverseOetf) < 1.5e-3) return { kind: 'bt709', approx: false };
  if (gammaErr < 2e-3) return { kind: 'gamma', gamma: Math.round(gamma * 1e4) / 1e4, approx: false };
  // Neither model fits: keep the exact curve, sampled at 4096 points.
  const curve = Array.from({ length: 4096 }, (_, i) => fn(i / 4095));
  return { kind: 'curve', gamma: Math.round(gamma * 1e4) / 1e4, curve, approx: false };
}

function xy(v: [number, number, number]): [number, number] {
  const s = v[0] + v[1] + v[2];
  return [v[0] / s, v[1] / s];
}

export function parseIcc(bytes: Uint8Array): IccInfo | null {
  if (bytes.length < 132) return null;
  const d = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (str4(d, 36) !== 'acsp') return null;
  const version = d.getUint8(8) + d.getUint8(9) / 16;
  const colorSpace = str4(d, 16).trim();
  const count = d.getUint32(128);
  const tags = new Map<string, { o: number; size: number }>();
  for (let i = 0; i < count && 132 + i * 12 + 12 <= bytes.length; i++) {
    const b = 132 + i * 12;
    tags.set(str4(d, b), { o: d.getUint32(b + 4), size: d.getUint32(b + 8) });
  }
  const tag = (s: string) => {
    const t = tags.get(s);
    return t && t.o + 8 <= bytes.length ? t : null;
  };
  const desc = tag('desc');
  const description = desc ? readText(d, desc.o, desc.size).trim() : '';
  const xyz = (s: string): [number, number, number] | null => {
    const t = tag(s);
    return t && str4(d, t.o) === 'XYZ ' ? [s15(d, t.o + 8), s15(d, t.o + 12), s15(d, t.o + 16)] : null;
  };
  let primaries: Chromaticities | null = null;
  const rX = xyz('rXYZ');
  const gX = xyz('gXYZ');
  const bX = xyz('bXYZ');
  if (rX && gX && bX) {
    // Colorants are adapted to the D50 PCS; undo the adaptation to get source chromaticities.
    let undo: Mat3;
    const chad = tag('chad');
    if (chad && str4(d, chad.o) === 'sf32') {
      const m = Array.from({ length: 9 }, (_, i) => s15(d, chad.o + 8 + 4 * i)) as Mat3;
      undo = mat3Inv(m);
    } else {
      const wt = xyz('wtpt');
      undo = wt ? bradford(D50, xy(wt)) : [1, 0, 0, 0, 1, 0, 0, 0, 1];
    }
    const r = mat3Vec(undo, rX);
    const g = mat3Vec(undo, gX);
    const b = mat3Vec(undo, bX);
    const w: [number, number, number] = [r[0] + g[0] + b[0], r[1] + g[1] + b[1], r[2] + g[2] + b[2]];
    primaries = { r: xy(r), g: xy(g), b: xy(b), w: xy(w) };
  }
  const trcTag = tag('rTRC') ?? tag('kTRC') ?? tag('gTRC');
  const curve = trcTag ? readCurve(d, trcTag.o) : null;
  return { description, colorSpace, version, primaries, trc: curve ? classifyCurve(curve) : null };
}

function samePrimaries(a: Chromaticities, b: Chromaticities, tol = 0.004): boolean {
  const k = ['r', 'g', 'b', 'w'] as const;
  return k.every((c) => Math.abs(a[c][0] - b[c][0]) < tol && Math.abs(a[c][1] - b[c][1]) < tol);
}

/** Match primaries against the built-in sets. */
export function matchPrimaries(c: Chromaticities): PrimariesId {
  for (const [id, p] of Object.entries(PRIMARIES)) if (samePrimaries(c, p.c)) return id as PrimariesId;
  return 'custom';
}

/** Turn a parsed ICC profile into an encoding hint. */
export function iccToHint(info: IccInfo): EncodingHint {
  const prim = info.primaries ? matchPrimaries(info.primaries) : info.colorSpace === 'GRAY' ? 'srgb' : 'srgb';
  const trc = info.trc ?? { kind: 'srgb' as const, approx: true };
  const parts = [`ICC profile “${info.description || 'unnamed'}”`];
  if (trc.kind === 'gamma') parts.push(`tone curve gamma ${trc.gamma}`);
  else if (trc.kind === 'curve') parts.push(`exact sampled tone curve (≈ gamma ${trc.gamma})`);
  else parts.push(`tone curve ${trc.kind}`);
  parts.push(
    prim === 'custom' ? 'custom primaries from colorant tags' : `primaries ${PRIMARIES[prim as Exclude<PrimariesId, 'custom'>].label}`,
  );
  return {
    transfer: trc.kind,
    gamma: trc.gamma,
    curve: trc.kind === 'curve' ? trc.curve : undefined,
    primaries: prim,
    chromaticities: prim === 'custom' ? info.primaries! : undefined,
    source: 'icc',
    description: parts.join('; '),
  };
}
